import defaultTo from 'lodash/defaultTo';
import * as yup from 'yup';

import type {
  InvestorFirmCreateDtoInput,
  UpdateInvestorFirmInput,
} from '../../../../../../graphql';
import { RolodexRecordStatus } from '../../../../../../graphql';
import type { InvestorFirmFragment } from '../../graphql/__generated__';

export const INVESTOR_FIRM_DETAIL_FIELD_LABELS: Record<
  keyof Omit<InvestorFirmCreateDtoInput, '__typename' | 'id' | 'status' | 'employees' | 'mpid'>,
  string
> = {
  firmKey: 'Firm Key',
  name: 'Firm Name',
  investorType: 'Firm Type',
  investorSubType: 'Firm Subtype',
  cik1: 'CIK (Primary)',
  cik2: 'CIK (Secondary)',
  crd: 'CRD',
  lei: 'LEI',
  factSet: 'FactSet',
  description: 'Description',
  website1: 'Website (Primary)',
  website2: 'Website (Secondary)',
  email1: 'Email (Primary)',
  email2: 'Email (Secondary)',
  phoneNumber1: 'Phone (Primary)',
  phoneNumber2: 'Phone (Secondary)',
  faxNumber1: 'Fax (Primary)',
  faxNumber2: 'Fax (Secondary)',
  address1: 'Address (Primary)',
  address2: 'Address (Secondary)',
};

export function getInvestorFirmDetailInitialValues(
  firm?: InvestorFirmFragment
): InvestorFirmCreateDtoInput {
  return {
    firmKey: defaultTo(firm?.firmKey, ''),
    name: defaultTo(firm?.name, ''),
    investorType: defaultTo(firm?.firmType, null),
    investorSubType: defaultTo(firm?.firmSubType, null),
    cik1: defaultTo(firm?.cik1, null),
    cik2: defaultTo(firm?.cik2, null),
    crd: defaultTo(firm?.crd, null),
    lei: defaultTo(firm?.lei, null),
    factSet: defaultTo(firm?.factSet, null),
    description: defaultTo(firm?.description, null),
    website1: defaultTo(firm?.website1, null),
    website2: defaultTo(firm?.website2, null),
    email1: defaultTo(firm?.emailAddress1, null),
    email2: defaultTo(firm?.emailAddress2, null),
    phoneNumber1: defaultTo(firm?.phone1, null),
    phoneNumber2: defaultTo(firm?.phone2, null),
    faxNumber1: defaultTo(firm?.fax1, null),
    faxNumber2: defaultTo(firm?.fax2, null),
    address1: defaultTo(firm?.address1, null),
    address2: defaultTo(firm?.address2, null),
    status: defaultTo(firm?.status, RolodexRecordStatus.Effective),
  };
}

export const getCikValidation = (fieldLabel: string) =>
  yup
    .string()
    .nullable()
    .length(10)
    .matches(/^\d+$/, `${fieldLabel} must be numeric`)
    .label(fieldLabel);

export const investorFirmValidationSchema = yup.object({
  cik1: getCikValidation(INVESTOR_FIRM_DETAIL_FIELD_LABELS.cik1),
  cik2: getCikValidation(INVESTOR_FIRM_DETAIL_FIELD_LABELS.cik2),
  lei: yup.string().nullable().length(20).label(INVESTOR_FIRM_DETAIL_FIELD_LABELS.lei),
  crd: yup
    .string()
    .nullable()
    .matches(/^\d+$/, `${INVESTOR_FIRM_DETAIL_FIELD_LABELS.crd} must be numeric`)
    .label(INVESTOR_FIRM_DETAIL_FIELD_LABELS.crd),
});

export const newInvestorFirmValidationSchema = yup
  .object({
    firmKey: yup.string().label(INVESTOR_FIRM_DETAIL_FIELD_LABELS.firmKey).trim().required(),
    name: yup.string().label(INVESTOR_FIRM_DETAIL_FIELD_LABELS.name).trim().required(),
  })
  .concat(investorFirmValidationSchema);

export function mapInvestorFirmToUpdateInput(
  firm: InvestorFirmFragment,
  status: RolodexRecordStatus
): Required<UpdateInvestorFirmInput> {
  return {
    name: firm.name,
    investorType: defaultTo(firm.firmType, null),
    investorSubType: defaultTo(firm.firmSubType, null),
    cik1: defaultTo(firm.cik1, null),
    cik2: defaultTo(firm.cik2, null),
    crd: defaultTo(firm.crd, null),
    lei: defaultTo(firm.lei, null),
    mpid: defaultTo(firm.mpid, null),
    factSet: defaultTo(firm.factSet, null),
    description: defaultTo(firm.description, null),
    website1: defaultTo(firm.website1, null),
    website2: defaultTo(firm.website2, null),
    email1: defaultTo(firm.emailAddress1, null),
    email2: defaultTo(firm.emailAddress2, null),
    phoneNumber1: defaultTo(firm.phone1, null),
    phoneNumber2: defaultTo(firm.phone2, null),
    faxNumber1: defaultTo(firm.fax1, null),
    faxNumber2: defaultTo(firm.fax2, null),
    address1: defaultTo(firm.address1, null),
    address2: defaultTo(firm.address2, null),
    status,
  };
}
